<template>
  <Toast position="bottom-right" />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import type { ToastMessageOptions } from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';

import { useLogout } from '@/composables/useLogout';
// import { useProfileStore } from '@/stores/profile';
// import { useRedirectStore } from '@/stores/redirect';
import { eventBus } from '@/configs/eventBus';
import router from '@/router';
// import { navigationItems } from '@/router/navigation';
import { routerPaths } from '@/router/router-path';

type ToastFingerprint = string;

// const LOGGEDOUT_ROUTES = ['login', 'entralogin', '403', '404'];
// const ALWAYS_ALLOWED_ROUTES = ['contactpersons']; // temorarily enabled as these pages might have fuzzy logic for now
const DEDUPE_WINDOW_MS = 2000;

const { t } = useI18n();

const toast = useToast();
const { logout } = useLogout();
// let handling403Promise: Promise<void> | null = null;

const recentToasts = new Map<ToastFingerprint, number>();

function buildFingerprint(opts: ToastMessageOptions): ToastFingerprint {
  // Keep it stable; avoid including functions/objects that change identity
  return [opts.severity ?? '', opts.summary ?? '', opts.detail ?? '', String(opts.life ?? '')].join(
    '|',
  );
}

const COALESCE_UPDATE_WINDOW_MS = 600;

let pendingUpdateFlushTimer: number | null = null;
let pendingUpdateToastCount = 0;
let pendingUpdateToastBase: ToastMessageOptions | null = null;

function flushPendingUpdateToast(): void {
  if (!pendingUpdateToastBase) return;

  const base = pendingUpdateToastBase;
  const count = pendingUpdateToastCount;

  pendingUpdateToastBase = null;
  pendingUpdateToastCount = 0;
  pendingUpdateFlushTimer = null;

  if (count <= 1) {
    showToast(base);
    return;
  }

  showToast({
    ...base,
    detail: t('toast.update.multi.detail', { count }),
    // summary: t('toast.update.multi.summary', { count }),
  });
}

function scheduleUpdateToast(nextBase: ToastMessageOptions): void {
  pendingUpdateToastCount += 1;
  pendingUpdateToastBase = nextBase;

  if (pendingUpdateFlushTimer != null) {
    window.clearTimeout(pendingUpdateFlushTimer);
  }

  pendingUpdateFlushTimer = window.setTimeout(() => {
    flushPendingUpdateToast();
  }, COALESCE_UPDATE_WINDOW_MS);
}

function shouldSuppressToast(opts: ToastMessageOptions): boolean {
  const fp = buildFingerprint(opts);
  const now = Date.now();
  const last = recentToasts.get(fp);

  // Cleanup (prevent unbounded growth)
  // Only keep entries that are still inside the dedupe window
  for (const [k, t] of recentToasts) {
    if (now - t > DEDUPE_WINDOW_MS) recentToasts.delete(k);
  }

  if (last != null && now - last < DEDUPE_WINDOW_MS) return true;

  recentToasts.set(fp, now);
  return false;
}

function showToast({
  severity = 'info',
  summary = 'Info',
  detail = '',
  life = 3000,
}: ToastMessageOptions = {}): void {
  const opts: ToastMessageOptions = { severity, summary, detail, life };

  if (shouldSuppressToast(opts)) return;

  toast.add(opts);
}

function getToastMessages(key: string): ToastMessageOptions {
  const messages: {
    [key: string]: {
      summary: string;
      detail?: string;
      life: number;
      severity: ToastMessageOptions['severity'];
    };
  } = {
    load: {
      severity: 'info',
      summary: t('toast.default.summary'),
      detail: t('toast.load.detail'),
      life: 3000,
    },
    send: {
      severity: 'info',
      summary: t('toast.default.summary'),
      detail: t('toast.send.detail'),
      life: 3000,
    },
    create: {
      severity: 'info',
      summary: t('toast.default.summary'),
      detail: t('toast.create.detail'),
      life: 3000,
    },
    update: {
      severity: 'info',
      summary: t('toast.default.summary'),
      detail: t('toast.update.detail'),
      life: 3000,
    },
    delete: {
      severity: 'info',
      summary: t('toast.default.summary'),
      detail: t('toast.delete.detail'),
      life: 3000,
    },
    error400: {
      severity: 'error',
      summary: t('toast.error'),
      life: 3000,
    },
    error403: {
      severity: 'error',
      summary: t('toast.error'),
      detail: t('toast.error403'),
      life: 3000,
    },
    error404: {
      severity: 'error',
      summary: t('toast.error'),
      detail: t('toast.error404'),
      life: 3000,
    },
    error409: {
      severity: 'error',
      summary: t('toast.error'),
      life: 3000,
    },
    error500: {
      severity: 'error',
      summary: t('toast.error'),
      life: 3000,
    },
    updatePosition: {
      severity: 'info',
      summary: t('toast.position.summary'),
      detail: t('toast.position.detail'),
      life: 3000,
    },
  };

  return (
    messages[key] || {
      severity: 'info',
      summary: t('unknown'),
      detail: t('unknown'),
      life: 3000,
    }
  );
}

type PaylodType = {
  summary?: string;
  message: string | Array<{ property: string; constraints: Record<string, string> }>;
};

function extractMessagesFromArray(
  messageArray: Array<{ property: string; constraints: Record<string, string> }>,
): string {
  const messages: string[] = [];
  messageArray.forEach((item) => {
    const constraintValues = Object.values(item.constraints || {});
    messages.push(...constraintValues);
  });
  return messages.join('; ') || 'Validation error';
}

function extractMessageDetail(message: PaylodType['message']): string {
  return typeof message === 'string' ? message : extractMessagesFromArray(message);
}

function extractErrorDetail(message: PaylodType['message']): string {
  if (typeof message === 'string') {
    return t(`toast.errors.${message}`);
  }
  if (Array.isArray(message)) {
    return extractMessagesFromArray(message);
  }
  return t('toast.errors.unknown');
}

const onLoad = () => {
  void showToast({ ...getToastMessages('load') });
};

const onSend = () => {
  void showToast({ ...getToastMessages('send') });
};

const onCreate = () => {
  void showToast({ ...getToastMessages('create') });
};

const onUpdate = (payload?: any) => {
  if (payload?.__fromNamespacedEvent) return; // ignore bubbled "update:ticket"
  const base = getToastMessages('update');
  scheduleUpdateToast(base);
};

const onDelete = () => {
  void showToast({ ...getToastMessages('delete') });
};

const onError400 = (payload: unknown) => {
  const typedPayload = payload as PaylodType;
  void showToast({
    ...getToastMessages('error400'),
    detail: extractErrorDetail(typedPayload.message),
  });
};

// function isAllowedByProfile(path: string, isSeyboldUser: boolean | undefined): boolean {
//   // Determine first route segment: "/contracts/123" -> "contracts"
//   const seg = (path.split('?')[0] ?? '').split('#')[0]?.split('/').filter(Boolean)[0] ?? '';

//   // Find the nav item for that segment
//   const matchingNavItem = navigationItems.find((navItem) => navItem.route === `/${seg}`);

//   // If no nav item found, be conservative: allow (guard will still decide on actual nav)
//   if (!matchingNavItem) return true;

//   const requiresSeyboldUser = !matchingNavItem.isCustomerAllowed;
//   const isAlwaysAllowedRoute =
//     LOGGEDOUT_ROUTES.includes(seg) || ALWAYS_ALLOWED_ROUTES.includes(seg);

//   if (isAlwaysAllowedRoute) return true;
//   if (!requiresSeyboldUser) return true;

//   return !!isSeyboldUser;
// }

/* route forbidden/not allowed */
const onError403 = async () => {
  void showToast({
    ...getToastMessages('error403'),
  });

  await router.push(routerPaths.dashboard); // redirect to dashboard to avoid being stuck on a page that keeps throwing 403
  return;

  // // Single-flight: do not run multiple 403 recoveries in parallel
  // if (handling403Promise) return handling403Promise;

  // handling403Promise = (async () => {
  //   const profileStore = useProfileStore();
  //   const redirectStore = useRedirectStore();

  //   const current = router.currentRoute.value;

  //   // Store return target only if we're not already on 403
  //   if (current.name !== '403') {
  //     redirectStore.set(current.fullPath);
  //   }

  //   // Always go to 403 once
  //   if (current.name !== '403') {
  //     await router.replace({ name: '403' });
  //   }

  //   const target = redirectStore.redirectUrl;
  //   if (!target) return;
  //   if (!profileStore.userProfile) return;

  //   // Avoid bounce loops
  //   const ok = isAllowedByProfile(target, profileStore.userProfile?.isSeyboldUser);
  //   if (!ok) return;

  //   redirectStore.clear();
  //   await router.replace(target);
  // })()
  //   .catch((e) => {
  //     // keep errors visible during dev, but don't break the global handler chain
  //     console.error('[auth] onError403 failed', e);
  //   })
  //   .finally(() => {
  //     handling403Promise = null;
  //   });

  // return handling403Promise;
};

const onError404 = (payload: unknown) => {
  const typedPayload = payload as PaylodType;
  void showToast({
    ...getToastMessages('error404'),
    detail: extractMessageDetail(typedPayload.message),
  });
};

/* token expired / invalid */
const onError401 = () => {
  logout({ redirect: true }); // logs out and redirects to entralogin
};

/* Validation exception */
const onError409 = (payload: unknown) => {
  const typedPayload = payload as PaylodType;
  void showToast({
    ...getToastMessages('error409'),
    detail: extractErrorDetail(typedPayload.message),
  });
};

const onError410 = () => {
  // TODO: something
};

const onError500 = (payload: unknown) => {
  const typedPayload = payload as PaylodType;
  void showToast({
    ...getToastMessages('error500'),
    detail: extractErrorDetail(typedPayload.message),
  });
};

const onMessage = (payload: unknown) => {
  const typedPayload = payload as PaylodType;
  void showToast({
    severity: 'info',
    summary: typedPayload.summary ?? t('toast.default.summary'),
    detail: extractMessageDetail(typedPayload.message),
    life: 3000,
  });
};

const onUpdatePosition = () => {
  void showToast({ ...getToastMessages('updatePosition') });
};

const onTriggerNotification = (payload: unknown) => {
  const typedPayload = payload as { id: string; params?: any };
  void showToast({
    severity: 'info',
    summary: t('toast.trigger.summary', { id: typedPayload.id, params: typedPayload.params }),
    detail: t('toast.trigger.detail', { id: typedPayload.id, params: typedPayload.params }),
    life: 4000,
  });
};

const onUpdateTicket = (payload: any) => {
  console.debug('[toast] onUpdateTicket fired', payload);
  if (payload?.silent) return;

  const override = payload?.toast as Partial<ToastMessageOptions> | undefined;
  const base = payload?.toast?.key
    ? getToastMessages(payload.toast.key)
    : getToastMessages('update');

  const finalToast: ToastMessageOptions = {
    ...base,
    ...(override ?? {}),
  };

  scheduleUpdateToast(finalToast);
};

const onError403Handler = () => void onError403();

onMounted(() => {
  eventBus.on('load', onLoad);
  eventBus.on('send', onSend);
  eventBus.on('create', onCreate);
  eventBus.on('update', onUpdate);
  eventBus.on('delete', onDelete);
  eventBus.on('error400', onError400);
  eventBus.on('error404', onError404);
  eventBus.on('error403', onError403Handler);
  eventBus.on('error401', onError401);
  eventBus.on('error409', onError409);
  eventBus.on('error410', onError410);
  eventBus.on('error500', onError500);
  eventBus.on('updatePosition', onUpdatePosition);
  eventBus.on('trigger-notification', onTriggerNotification);
  eventBus.on('message', onMessage);
  eventBus.on('update:ticket', onUpdateTicket);
});

onUnmounted(() => {
  if (pendingUpdateFlushTimer != null) {
    window.clearTimeout(pendingUpdateFlushTimer);
    pendingUpdateFlushTimer = null;
  }
  pendingUpdateToastCount = 0;
  pendingUpdateToastBase = null;

  eventBus.off('load', onLoad);
  eventBus.off('send', onSend);
  eventBus.off('create', onCreate);
  eventBus.off('update', onUpdate);
  eventBus.off('delete', onDelete);
  eventBus.off('error400', onError400);
  eventBus.off('error403', onError403Handler);
  eventBus.off('error401', onError401);
  eventBus.off('error409', onError409);
  eventBus.off('error410', onError410);
  eventBus.off('error500', onError500);
  eventBus.off('updatePosition', onUpdatePosition);
  eventBus.off('trigger-notification', onTriggerNotification);
  eventBus.off('message', onMessage);
  eventBus.off('update:ticket', onUpdateTicket);
});
</script>
